import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, Menu, MessageCircle, X } from "lucide-react";
import { imgUrl } from "@/lib/api";
import { useSite, waLinkFor } from "@/lib/SiteContext";

export default function Header({ onNavigate }) {
  const { brand, nav, contact } = useSite();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    fn();
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const go = (url) => {
    setOpen(false);
    if (url.startsWith("#")) onNavigate(url);
    else if (url.startsWith("/")) navigate(url);
    else window.open(url, "_blank", "noopener");
  };

  const testId = (label) => label.toLowerCase().replace(/\s+/g, "-");

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-500 ${
        scrolled
          ? "border-line/70 bg-cream/85 backdrop-blur-xl"
          : "border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 md:px-12">
        <button
          data-testid="header-logo"
          onClick={() => go("#home")}
          className="flex items-center gap-2"
          aria-label={`${brand.name} — home`}
        >
          {brand.logo ? (
            <img src={imgUrl(brand.logo)} alt={brand.name} className="h-9 w-auto object-contain" />
          ) : (
            <Heart size={20} className="fill-rose text-rose" />
          )}
          <span className="font-serif text-2xl font-semibold tracking-tight text-ink">
            {brand.name}
          </span>
        </button>

        <nav className="hidden items-center gap-9 lg:flex" aria-label="Primary">
          {nav.map((link, i) => (
            <button
              key={link.id || i}
              data-testid={`nav-link-${testId(link.label)}`}
              onClick={() => go(link.url)}
              className="group relative text-sm font-medium text-ink/70 transition-colors hover:text-ink"
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-rose transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="header-enquire-button"
            href={waLinkFor(contact)}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-medium text-cream transition-colors duration-300 hover:bg-rose hover:text-white sm:inline-flex"
          >
            <MessageCircle size={15} /> {brand.cta_label}
          </a>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen((v) => !v)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-line bg-white/60 text-ink lg:hidden"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-line/60 bg-cream/95 backdrop-blur-xl lg:hidden"
            aria-label="Mobile"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {nav.map((link, i) => (
                <motion.button
                  key={link.id || i}
                  data-testid={`mobile-nav-link-${testId(link.label)}`}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                  onClick={() => go(link.url)}
                  className="py-3 text-left font-serif text-3xl font-medium text-ink transition-colors hover:text-rose"
                >
                  {link.label}
                </motion.button>
              ))}
              <a
                data-testid="mobile-enquire-button"
                href={waLinkFor(contact)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-4 text-sm font-semibold text-white transition-colors hover:bg-[#1eb857]"
              >
                <MessageCircle size={16} /> {brand.cta_label}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
